<template>
  <div>
    <h3 class="subtitle1 text-primary mb-4" data-testid="blocked-email-reports-title">
      {{ $t("emailSettings.blockedEmailReportsConfig.title") }}
    </h3>
    <v-card class="pa-6">
      <div class="d-flex align-center">
        <v-checkbox
          v-model="enabledModel"
          :disabled="disabled"
          :ripple="false"
          color="primary"
          data-testid="enable-blocked-email-reports-checkbox"
        >
          <template #label>
            <div
              class="subtitle1"
              :class="{ 'text-indigo-medium': disabled }"
              v-text="$t('emailSettings.blockedEmailReportsConfig.enableToggle')"
            />
          </template>
        </v-checkbox>
      </div>

      <template v-if="localValue.blockedEmailReportsConfig.enabled">
        <email-report-config-section
          v-model="localValue.blockedEmailReportsConfig"
          :disabled="disabled"
          :context="EmailReportConfigContext.BLOCKED_EMAIL_REPORTS"
          :is-global-policy="isGlobalPolicy"
        />
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, toRefs, computed } from "vue";
import type { PropType } from "vue";
import type {
  EmailSecuritySettings,
  BlockedEmailReportUser,
} from "@/_store/email-security/email-settings.module.ts";
import { EmailReportConfigContext, EmailSettingsAction } from "@/constants/emails.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import AddAdminEmailAddressModal from "@/components/modals/email-settings/AddAdminEmailAddressModal.vue";
import EmailReportConfigSection from "@/components/EmailReportConfigSection.vue";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  components: { EmailReportConfigSection },
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isGlobalPolicy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const dialogsStore = useDialogsStore();

    const localValue = useTwoWayBinding(modelValue, (value: EmailSecuritySettings) =>
      emit("update:modelValue", value)
    );

    const enabledModel = computed({
      get: () => localValue.value.blockedEmailReportsConfig.enabled,
      set: (value: boolean) => {
        if (value && !localValue.value.adminEmail) {
          openAddAdminEmailModal();
          return;
        }
        localValue.value.blockedEmailReportsConfig.enabled = value;
      },
    });

    const openAddAdminEmailModal = () => {
      const { openDialog } = dialogsStore;
      const isEditMode = localValue.value.adminEmail;
      const action = isEditMode
        ? EmailSettingsAction.EDIT_ADMIN_EMAIL_ADDRESS
        : EmailSettingsAction.ADD_ADMIN_EMAIL_ADDRESS;

      openDialog({
        ...componentDialogsConfigConstructor({
          callback: (user: BlockedEmailReportUser) => {
            localValue.value.adminEmail = user.email;
            localValue.value.blockedEmailReportsConfig.enabled = true;
          },
          width: ModalWidth.MEDIUM,
          component: AddAdminEmailAddressModal,
          action,
          item: isEditMode
            ? ({
                email: localValue.value.adminEmail,
                userId: "",
              } as BlockedEmailReportUser)
            : null,
        }),
      });
    };

    return {
      localValue,
      enabledModel,
      openAddAdminEmailModal,
      EmailReportConfigContext,
    };
  },
});
</script>
